import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal, untracked, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartType } from '@data/_generated-api/platform-api/models/platform-chart-type';
import { PlatformChartVisibility } from '@data/_generated-api/platform-api/models/platform-chart-visibility';
import { SupportCommentComposerComponent, SupportCommentPayload } from '@/app/support/support-comment-composer.component';
import { HdChartGroupService } from '../../library/hd-chart-group.service';
import { HdChartLibraryService } from '../../library/hd-chart-library.service';
import { buildChartDataSourceQuery } from '../../library/hd-chart-datasource-query';
import { extractCycleSourceCore } from '../../library/hd-cycle-chart.utils';
import { buildMomentChartName, buildTransitSnapshotPreset } from '../../library/hd-transit-chart.utils';
import { HdColorPickerComponent } from '../../library/hd-color-picker.component';
import { HdDotPickerComponent, HdDotPickerOption } from '../../library/hd-dot-picker.component';
import { HdIconPickerComponent } from '../../library/hd-icon-picker.component';
import { HdBodygraphCenter, HdBodygraphChartPreset } from '../hd-bodygraph.models';
import { filterHdJustNowPersonalityActivations } from '../../mandala/just-now-transit/hd-just-now-transit';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HdIChingAccessService } from '../../../iching/hd-iching-access.service';
import { HdIChingConsultRequest } from '../../../iching/moment/hd-iching-moment.models';
import { HdBodygraphPlanetaryActivation } from '../hd-bodygraph.models';
import { HdBodygraphRenderStateFactory } from '../hd-bodygraph-render-state.factory';
import { HdMindFieldsComponent } from './hd-mind-fields.component';
import { HdMindGateSuggestion, buildHdMindCenterOptions, buildHdMindGateOptions, buildHdMindGateSuggestions } from './hd-mind-conditioners';
import { HD_MIND_MAX_CONDITIONERS, HdMindConditioner, hdMindConditionerText, joinHdMomentExtra } from './hd-moment-extra';
import {
  HD_MIND_MOMENT_DEFAULT_COLOR,
  HD_MIND_MOMENT_DEFAULT_ICON,
  HD_MOMENT_CATEGORIES,
  HD_MOMENT_EMOTIONS,
  HD_MOMENT_MAX_EMOTIONS,
  HD_MOOD_COLORS,
  HD_MOOD_VALUES,
  HD_VITALITY_ICONS,
  HD_VITALITY_VALUES,
  hdEmotionLabelKey,
  hdMomentCategoryLabelKey,
  hdMoodLabelKey,
  hdSignpostCounterpart,
  hdVitalityLabelKey,
  joinHdMomentEmotions,
  toggleHdMomentEmotion,
} from './hd-moment-fields';

/** null = gesloten. */
export interface HdMomentLinkTarget {
  chartId: string;
  chartName: string;
}

/** Maximum van `ChartRelation.Note` (backend ModelConstraints, verruimd voor volwaardige notities). */
const MOMENT_NOTE_MAX_LENGTH = 10_000;

/**
 * Smaken van de dialoog (herinrichting 2026-10-24): wat wil je loggen? De smaak
 * bepaalt de velden en de categorie: `thought` (gedachte & dagboek, daily, met het
 * mind-blok), `mood` (stemming & vitaliteit, daily), `lifetime` (levensloop, met
 * einde), `detailed` (alles kiesbaar), `iching` (raadpleging starten) en `chart`
 * (bestaande chart als moment linken, enkel met een vaste bronchart).
 */
export type HdMomentLinkMode = 'chart' | 'thought' | 'mood' | 'lifetime' | 'detailed' | 'iching';

/**
 * De smaak Gedetailleerd (alles kiesbaar) blijft in de code maar staat voorlopig verborgen
 * (Steven 2026-10-24): de drie gerichte smaken dekken het gebruik; verwijderen volgt later.
 */
const HD_MOMENT_DETAILED_MODE_VISIBLE = false;

/** De smaken die een moment op datum bewaren (naam, tekst, gegevens onderaan). */
const HD_MOMENT_DATE_MODES: readonly HdMomentLinkMode[] = ['thought', 'mood', 'lifetime', 'detailed'];

export function isHdMomentDateMode(mode: HdMomentLinkMode | null | undefined): mode is 'thought' | 'mood' | 'lifetime' | 'detailed' {
  return mode != null && HD_MOMENT_DATE_MODES.includes(mode);
}

/** Bij te zetten rijen in de gedachte-smaak (standaard verborgen). */
type HdMomentThoughtExtra = 'emotions' | 'mood' | 'vitality';

/**
 * Moment toevoegen (spec notable moments §4.6), in twee smaken:
 *  1. **Bestaande chart als moment** — kies eender welke chart; legt alleen de link.
 *  2. **Moment op datum** — berekent de transit van dat tijdstip via het
 *     bestaande compute-pad en bewaart hem als snapshot-chart; een datum
 *     zónder uur wordt een dag-range (00:00 t/m 23:59 lokale tijd, de
 *     beleefbare viewer volgt in de range-ronde).
 * Beide smaken dragen een optionele notitie mét sumiere opmaak en
 * afbeeldingen (dezelfde composer als de support-reacties); de notitie wordt
 * straks ook getoond in het Notities-paneel van de geopende chart.
 *
 * **Bronkiezer-modus** (`openWithSourcePicker`, transits-ingang, spec
 * 2026-09-26 §2.7): er is nog geen bronchart; de dialoog laat er één kiezen
 * uit de standaardcharts, met de eigen chart (isMyChart) vooraan en
 * voorgeselecteerd, laadt het document als natal-kern en biedt enkel de
 * datum-smaak aan (een moment in de tijd hoort bij een chart).
 *
 * **Derde smaak "Klassieke I Tjing"** (spec 2026-10-18 I Tjing-momenten
 * §3.6, enkel met `classicVisible()`): datum en tijd, en een knop
 * "Raadpleging starten" die `ichingRequested` meldt; de host opent dan de
 * I Tjing-momentdialoog. Geen momentdetails of notitie: dat zijn chartvelden.
 */
@Component({
  selector: 'hd-moment-link-dialog',
  standalone: true,
  imports: [
    SharedModule, FormsModule, NgSelectModule, SupportCommentComposerComponent, HdColorPickerComponent, HdDotPickerComponent,
    HdIconPickerComponent, HdMindFieldsComponent,
  ],
  templateUrl: './hd-moment-link-dialog.component.html',
  styleUrl: './hd-moment-link-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdMomentLinkDialogComponent {
  private readonly groupService = inject(HdChartGroupService);
  private readonly library = inject(HdChartLibraryService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly renderStateFactory = inject(HdBodygraphRenderStateFactory);

  readonly target = model<HdMomentLinkTarget | null>(null);
  /** Preset van de bronchart (natal-kern voor de datum-smaak); zonder preset is alleen smaak 1 beschikbaar. */
  readonly sourcePreset = input<HdBodygraphChartPreset | null>(null);
  /**
   * Voorgevulde datum (UTC-iso) voor "toevoegen vanaf de tijdlijn": de dialog
   * opent dan meteen in de datum-smaak met die dag ingevuld (stap 5).
   */
  readonly prefillDateUtc = input<string | null>(null);
  /**
   * Voorgeselecteerde categorie per ingang (spec levenstijdlijn §2.7): de tijdlijn
   * geeft 'lifetime' (smaak Levensloop), de kalender 'daily' en de brein-knop 'mind'
   * (smaak Gedachte); null = Gedachte. `initialMode` gaat voor.
   */
  readonly defaultCategory = input<string | null>(null);
  /**
   * Opent de dialoog zonder vaste bronchart: de gebruiker kiest de chart
   * (standaardcharts, eigen chart voorgeselecteerd). Alternatief voor `target`.
   */
  readonly openWithSourcePicker = model(false);
  /** Ook het uur van `prefillDateUtc` voorinvullen (een wissel/tijdstip i.p.v. een dag). */
  readonly prefillWithTime = input(false);
  /** Smaak die bij het openen voorgeselecteerd staat (bv. `'iching'` vanuit het I Tjing-dagpaneel, `'chart'` vanuit "chart linken"). */
  readonly initialMode = input<HdMomentLinkMode | null>(null);
  /** Emit de chart-id na elke geslaagde toevoeging (de sectie ververst dan). */
  readonly changed = output<string>();
  /** Emit de id van een NIEUW aangemaakt moment (datum-smaak): de eigenaar opent die standaard. */
  readonly chartCreated = output<string>();
  /** "Raadpleging starten" in de I Tjing-smaak: chart en tijdstip voor de I Tjing-momentdialoog. */
  readonly ichingRequested = output<HdIChingConsultRequest>();

  /** Kandidaten voor de chart-picker (volledige bibliotheek, alle typen). */
  private readonly candidates = signal<PlatformChartListItemDto[]>([]);
  protected readonly mode = signal<HdMomentLinkMode>('chart');
  /** Eén van de datum-smaken (naam, tekst, inhoud per smaak, gegevens onderaan). */
  protected readonly dateMode = computed(() => isHdMomentDateMode(this.mode()));
  /** Gedachte: bijgezette rijen (stemming, vitaliteit, emoties). */
  private readonly thoughtExtras = signal<ReadonlySet<HdMomentThoughtExtra>>(new Set());
  /** De I Tjing-smaak bestaat enkel met Classic I Ching-toegang (feature én setting). */
  protected readonly ichingAvailable = computed(() => this.ichingAccess.classicVisible());
  protected readonly newChartId = signal<string | null>(null);
  /** Datum-smaak: "YYYY-MM-DD" + optioneel "HH:mm"; naam is vrij bewerkbaar na de prefill. */
  protected readonly dateValue = signal('');
  protected readonly timeValue = signal('');
  /** Optioneel einde (stap 4): mét einddatum wordt het moment een range voor de tijdslider. */
  protected readonly dateEndValue = signal('');
  protected readonly timeEndValue = signal('');
  protected readonly nameValue = signal('');
  /** Laat de default-naam meebewegen tot de gebruiker zelf typt. */
  private nameEdited = false;
  protected readonly loading = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal('');

  // Momentdetails (spec levenstijdlijn §2.2/§2.3/§2.7) — alle optioneel.
  protected readonly colorValue = signal<string | null>(null);
  protected readonly categoryValue = signal<string | null>(null);
  protected readonly moodValue = signal<number | null>(null);
  protected readonly vitalityValue = signal<number | null>(null);
  protected readonly emotionKeys = signal<readonly string[]>([]);
  protected readonly iconValue = signal<string | null>(null);
  /** De icoonpicker is fors (zoekveld + grid) en staat daarom achter een toggle. */
  protected readonly iconPickerOpen = signal(false);

  // ── Mind-notitie (spec 2026-10-22-mind-logboek §4; ronde 2 2026-10-24: transits eerst,
  //    meerdere conditioneerders, centrum volgt, de rest achter Verfijnen) ────────────
  protected readonly mindCenter = signal<HdBodygraphCenter | null>(null);
  protected readonly mindGate = signal<number | null>(null);
  /** Centrum door de gebruiker zelf gekozen (onder Verfijnen); anders volgt het de eerste transitkeuze. */
  private mindCenterManual = false;
  /** Gekozen transitsuggesties (sleutels planeet:poort:lijn), in volgorde van aantikken. */
  protected readonly mindTransitKeys = signal<readonly string[]>([]);
  /** Uitklap Verfijnen (centrum, poort, persoon); dicht bij elke opening. */
  protected readonly mindRefineOpen = signal(false);
  protected readonly mindMaxConditioners = HD_MIND_MAX_CONDITIONERS;
  private readonly composer = viewChild(SupportCommentComposerComponent);
  /** Transitactivaties op het ingevulde tijdstip; null = (nog) niet berekend. */
  private readonly mindTransitActivations = signal<readonly HdBodygraphPlanetaryActivation[] | null>(null);
  protected readonly mindTransitLoading = signal(false);
  private mindTransitSeq = 0;
  protected readonly mindPersonChartId = signal<string | null>(null);
  private readonly mindPersonPreset = signal<{ chartId: string; chartName: string; preset: HdBodygraphChartPreset } | null>(null);
  protected readonly mindPersonLoading = signal(false);
  /** Aangevinkte poorten van de persoon (sleutel poort). */
  protected readonly mindPersonGates = signal<readonly number[]>([]);

  /** Het mind-blok hoort bij de smaak Gedachte, en bij Gedetailleerd (alles kiesbaar). */
  protected readonly mindActive = computed(() => this.mode() === 'thought' || this.mode() === 'detailed');

  /** De categorie die bewaard wordt: Gedachte en Stemming zijn daily, Levensloop lifetime, Gedetailleerd kiest zelf. */
  protected readonly effectiveCategory = computed<string | null>(() => {
    switch (this.mode()) {
      case 'thought':
      case 'mood':
        return 'daily';
      case 'lifetime':
        return 'lifetime';
      case 'detailed':
        return this.categoryValue();
      default:
        return null;
    }
  });

  protected readonly showVitality = computed(() => this.mode() === 'mood' || this.mode() === 'detailed' || this.thoughtExtras().has('vitality'));
  protected readonly showMood = computed(() => this.mode() === 'mood' || this.mode() === 'detailed' || this.thoughtExtras().has('mood'));
  protected readonly showEmotions = computed(() => this.mode() === 'mood' || this.mode() === 'detailed' || this.thoughtExtras().has('emotions'));
  /** Een einde (periode) hoort bij de levensloop en bij het volledige formulier. */
  protected readonly showEndDate = computed(() => this.mode() === 'lifetime' || this.mode() === 'detailed');

  /** Gedachte: emoties, stemming en vitaliteit als toggles (open = actief; dichtklikken wist de waarde). */
  protected readonly thoughtExtraOptions = computed<{ key: HdMomentThoughtExtra; label: string; open: boolean }[]>(() => {
    const t = this.text();
    const extras = this.thoughtExtras();
    return [
      { key: 'emotions' as const, label: t.emotionsLabel, open: extras.has('emotions') },
      { key: 'mood' as const, label: t.moodLabel, open: extras.has('mood') },
      { key: 'vitality' as const, label: t.vitalityLabel, open: extras.has('vitality') },
    ];
  });

  /** De smaakknoppen in vaste volgorde; wat niet kan (geen natal-kern, geen I Tjing, bronkiezer) valt weg. */
  protected readonly modeOptions = computed<{ value: HdMomentLinkMode; label: string; icon: string }[]>(() => {
    const t = this.text();
    const dateModes = this.dateModeAvailable() || this.pickerMode();
    const options: { value: HdMomentLinkMode; label: string; icon: string; available: boolean }[] = [
      { value: 'thought', label: t.modeThought, icon: 'fas fa-brain', available: dateModes },
      { value: 'mood', label: t.modeMood, icon: 'fas fa-face-smile', available: dateModes },
      { value: 'lifetime', label: t.modeLifetime, icon: 'fas fa-timeline', available: dateModes },
      { value: 'iching', label: t.modeIChing, icon: 'fa-regular fa-scroll-old', available: this.ichingAvailable() },
      { value: 'detailed', label: t.modeDetailed, icon: 'fas fa-sliders', available: dateModes && HD_MOMENT_DETAILED_MODE_VISIBLE },
      { value: 'chart', label: t.modeChart, icon: 'fas fa-link', available: !this.pickerMode() },
    ];
    return options.filter(option => option.available).map(({ value, label, icon }) => ({ value, label, icon }));
  });

  protected readonly mindCenterOptions = computed(() => {
    const preset = this.effectiveSourcePreset();
    return preset ? buildHdMindCenterOptions(preset.configuration, this.renderStateFactory) : [];
  });

  protected readonly mindGateOptions = computed(() => {
    const preset = this.effectiveSourcePreset();
    const center = this.mindCenter();
    return preset && center ? buildHdMindGateOptions(preset.configuration, center) : [];
  });

  /**
   * Transitsuggesties: de activaties van het tijdstip in alle open centra (daar komt
   * conditionering binnen), plus die in een zelf gekozen gedefinieerd centrum
   * (hangende poort). Zo blijven de chips van andere centra aantikbaar na een keuze.
   */
  protected readonly mindTransitSuggestions = computed<HdMindGateSuggestion[]>(() => {
    const preset = this.effectiveSourcePreset();
    const activations = this.mindTransitActivations();
    if (!preset || !activations) return [];
    const build = (center: HdBodygraphCenter | null) => buildHdMindGateSuggestions({
      ownConfiguration: preset.configuration,
      otherActivations: activations,
      renderStateFactory: this.renderStateFactory,
      center,
      kind: 'transit',
    });
    const open = build(null);
    const chosen = this.mindCenter();
    if (!chosen || this.mindCenterOptions().some(option => option.center === chosen && option.open)) return open;
    const seen = new Set(open.map(mindSuggestionKey));
    return [...open, ...build(chosen).filter(suggestion => !seen.has(mindSuggestionKey(suggestion)))];
  });

  /** De aangetikte transitchips, in volgorde van aantikken (voor opslag en "Zet in tekst"). */
  protected readonly mindSelectedTransits = computed<HdMindGateSuggestion[]>(() => {
    const suggestions = this.mindTransitSuggestions();
    return this.mindTransitKeys()
      .map(key => suggestions.find(suggestion => mindSuggestionKey(suggestion) === key))
      .filter((suggestion): suggestion is HdMindGateSuggestion => suggestion != null);
  });

  /** Aantal conditioneerders (transits + eventueel de persoon), begrensd door het plafond. */
  protected readonly mindConditionerCount = computed(() =>
    this.mindSelectedTransits().length + (this.mindPersonPreset() ? 1 : 0));
  protected readonly mindFull = computed(() => this.mindConditionerCount() >= HD_MIND_MAX_CONDITIONERS);

  /** Samenvatting naast de dichte Verfijnen-knop: centrum · poort · persoon. */
  protected readonly mindRefineSummary = computed(() => {
    const t = this.text();
    const parts: string[] = [];
    const center = this.mindCenter();
    if (center) parts.push(t.mindCenterName(center));
    const gate = this.mindGate();
    if (gate != null) parts.push(t.mindGate(gate));
    const person = this.mindPersonPreset();
    if (person) parts.push(person.chartName);
    return parts.join(' · ');
  });

  /** Persoonssuggesties: de activaties van de gekozen chart in het gekozen centrum (of alle open centra). */
  protected readonly mindPersonSuggestions = computed<HdMindGateSuggestion[]>(() => {
    const preset = this.effectiveSourcePreset();
    const person = this.mindPersonPreset();
    if (!preset || !person) return [];
    return buildHdMindGateSuggestions({
      ownConfiguration: preset.configuration,
      otherActivations: person.preset.planetaryActivations ?? [],
      renderStateFactory: this.renderStateFactory,
      center: this.mindCenter(),
      kind: 'person',
    });
  });

  /** Kandidaat-personen: standaardcharts uit de bibliotheek, de bronchart zelf niet. */
  protected readonly mindPersonOptions = computed(() => {
    const target = this.effectiveTarget();
    const pool = this.candidates().length > 0 ? this.candidates() : this.sourceCandidates();
    return pool
      .filter(chart => chart.id && chart.id !== target?.chartId && chart.type === PlatformChartType.Standard)
      .map(chart => ({ value: chart.id as string, label: chart.name ?? '' }));
  });

  /** Het tijdstip (UTC-iso) waarvoor de transitsuggesties gelden: de ingevulde tijd, anders 12:00 lokaal. */
  private readonly mindTransitMomentUtc = computed<string | null>(() => {
    const date = this.dateValue();
    if (!date) return null;
    const local = moment(`${date}T${this.timeValue() || '12:00'}`, 'YYYY-MM-DDTHH:mm', true);
    return local.isValid() ? local.toISOString() : null;
  });

  protected readonly categories = HD_MOMENT_CATEGORIES;
  protected readonly vitalityValues = HD_VITALITY_VALUES;
  protected readonly vitalityIcons = HD_VITALITY_ICONS;
  protected readonly maxEmotions = HD_MOMENT_MAX_EMOTIONS;

  /** Stemming als gedeelde dot-picker (zelfde bolletjes-selector als de kleuren). */
  protected readonly moodOptions = computed<readonly HdDotPickerOption[]>(() =>
    HD_MOOD_VALUES.map(mood => ({
      id: String(mood),
      color: HD_MOOD_COLORS[mood] ?? '',
      label: this.detailLabels().mood[mood],
    })));

  protected readonly moodDotValue = computed(() => {
    const mood = this.moodValue();
    return mood != null ? String(mood) : null;
  });

  protected onMoodPicked(id: string | null): void {
    this.moodValue.set(id == null ? null : Number(id));
  }

  /** Vertaalde labels voor de detailvelden, in de content-taal (zelfde patroon als text()). */
  protected readonly detailLabels = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      category: Object.fromEntries(HD_MOMENT_CATEGORIES.map(category =>
        [category, translate(hdMomentCategoryLabelKey(category))])) as Record<string, string>,
      categoryHint: Object.fromEntries(HD_MOMENT_CATEGORIES.map(category =>
        [category, translate(`humanDesign.momentFields.categoryHint.${category}`)])) as Record<string, string>,
      mood: Object.fromEntries(HD_MOOD_VALUES.map(mood =>
        [mood, translate(hdMoodLabelKey(mood))])) as Record<number, string>,
      vitality: Object.fromEntries(HD_VITALITY_VALUES.map(vitality =>
        [vitality, translate(hdVitalityLabelKey(vitality))])) as Record<number, string>,
      emotion: Object.fromEntries(HD_MOMENT_EMOTIONS.map(emotion =>
        [emotion.key, translate(hdEmotionLabelKey(emotion.key))])) as Record<string, string>,
    };
  });

  /** Emotie-chips in de vaste volgorde: de vier signpost-paren eerst, dan de rest. */
  protected readonly emotionOptions = HD_MOMENT_EMOTIONS;

  // ── Bronkiezer-modus ─────────────────────────────────────────────────────

  /** Kandidaat-broncharts: standaardcharts, de eigen chart vooraan. */
  private readonly sourceCandidates = signal<PlatformChartListItemDto[]>([]);
  protected readonly sourceChartId = signal<string | null>(null);
  /** Gekozen bronchart + zijn document (natal-kern voor de datum-smaak). */
  private readonly pickedSource = signal<{ target: HdMomentLinkTarget; preset: HdBodygraphChartPreset } | null>(null);
  protected readonly sourceLoading = signal(false);

  /** Bronkiezer actief: geopend zonder vaste bronchart. */
  protected readonly pickerMode = computed(() => this.target() == null && this.openWithSourcePicker());

  /** De chart waarvoor het moment bewaard wordt: de vaste bron of de gekozen chart. */
  private readonly effectiveTarget = computed<HdMomentLinkTarget | null>(
    () => this.target() ?? this.pickedSource()?.target ?? null,
  );

  private readonly effectiveSourcePreset = computed<HdBodygraphChartPreset | null>(() =>
    this.target() != null ? this.sourcePreset() : this.pickedSource()?.preset ?? null,
  );

  protected readonly sourceChartOptions = computed(() =>
    this.sourceCandidates()
      .filter(chart => !!chart.id)
      .map(chart => ({ value: chart.id as string, label: chart.name ?? '' })),
  );

  protected readonly dialogTitleData = computed(() => this.effectiveTarget()?.chartName ?? '');

  constructor() {
    effect(() => {
      const target = this.target();
      if (!target) return;
      this.resetForm();
      untracked(() => this.openInInitialMode());
      void this.loadCandidates(target.chartId);
    });

    effect(() => {
      if (!this.openWithSourcePicker()) return;
      untracked(() => {
        if (this.target() != null) return;
        this.resetForm();
        this.sourceCandidates.set([]);
        this.sourceChartId.set(null);
        this.pickedSource.set(null);
        this.openInInitialMode();
        void this.loadSourceCandidates();
      });
    });

    // Transitsuggesties volgen het tijdstip zolang het Mind-blok open staat; dezelfde
    // berekening als het snapshot zelf (calculateFromUtc), één call per tijdstip.
    effect(() => {
      const active = this.mindActive();
      const momentUtc = this.mindTransitMomentUtc();
      const preset = this.effectiveSourcePreset();
      untracked(() => {
        if (!active || !momentUtc || !preset) {
          this.mindTransitActivations.set(null);
          return;
        }
        void this.loadMindTransitActivations(preset, momentUtc);
      });
    });
  }

  /** Alle formuliervelden terug naar leeg (bij elke nieuwe opening). */
  private resetForm(): void {
    this.mode.set('chart');
    this.newChartId.set(null);
    this.dateValue.set('');
    this.timeValue.set('');
    this.dateEndValue.set('');
    this.timeEndValue.set('');
    this.nameValue.set('');
    this.nameEdited = false;
    this.error.set('');
    this.colorValue.set(null);
    this.moodValue.set(null);
    this.vitalityValue.set(null);
    this.emotionKeys.set([]);
    this.iconValue.set(null);
    this.iconPickerOpen.set(false);
    this.categoryValue.set(null);
    this.thoughtExtras.set(new Set());
    this.resetMind();
  }

  /**
   * Smaak bij het openen: `initialMode` gaat voor, anders volgt de smaak de
   * `defaultCategory` (lifetime → Levensloop, al de rest → Gedachte). Zonder
   * natal-kern (vaste bronchart zonder preset) blijft enkel "bestaande chart" over.
   * Datum en tijd staan standaard op nu, tenzij de ingang een tijdstip meegeeft.
   */
  private openInInitialMode(): void {
    const requested = this.initialMode();
    const category = this.defaultCategory();
    if (category === 'daily' || category === 'lifetime') this.categoryValue.set(category);
    if (requested === 'iching' && this.ichingAvailable()) {
      this.setMode('iching');
      return;
    }
    if (requested === 'chart' && !this.pickerMode()) {
      this.mode.set('chart');
      return;
    }
    if (!this.dateModeAvailable() && !this.pickerMode()) {
      this.mode.set('chart');
      return;
    }
    this.mode.set(isHdMomentDateMode(requested) ? requested : category === 'lifetime' ? 'lifetime' : 'thought');
    this.applyThoughtDefaults();
    this.applyPrefill();
  }

  /**
   * Gedachte: brein-icoon en violet als default (zolang ze leeg zijn); bij het
   * verlaten van de smaak gaan die defaults weer weg als ze onaangeroerd bleven.
   */
  private applyThoughtDefaults(): void {
    if (this.mode() === 'thought') {
      if (!this.iconValue()) this.iconValue.set(HD_MIND_MOMENT_DEFAULT_ICON);
      if (!this.colorValue()) this.colorValue.set(HD_MIND_MOMENT_DEFAULT_COLOR);
      return;
    }
    if (this.iconValue() === HD_MIND_MOMENT_DEFAULT_ICON) this.iconValue.set(null);
    if (this.colorValue() === HD_MIND_MOMENT_DEFAULT_COLOR) this.colorValue.set(null);
  }

  /** Rij bijzetten of weer dichtklappen; dicht = de waarde gaat mee weg (wat je niet ziet, bewaar je niet). */
  protected toggleThoughtExtra(extra: HdMomentThoughtExtra): void {
    const next = new Set(this.thoughtExtras());
    if (next.has(extra)) {
      next.delete(extra);
      if (extra === 'emotions') this.emotionKeys.set([]);
      if (extra === 'mood') this.moodValue.set(null);
      if (extra === 'vitality') this.vitalityValue.set(null);
    } else {
      next.add(extra);
    }
    this.thoughtExtras.set(next);
  }

  /** Toevoegknop onderaan: de composer verzamelt tekst en afbeeldingen en meldt zich via `send`. */
  protected submitFromFooter(): void {
    void this.composer()?.submit();
  }

  private resetMind(): void {
    this.mindCenter.set(null);
    this.mindGate.set(null);
    this.mindCenterManual = false;
    this.mindTransitKeys.set([]);
    this.mindRefineOpen.set(false);
    this.mindTransitActivations.set(null);
    this.mindPersonChartId.set(null);
    this.mindPersonPreset.set(null);
    this.mindPersonGates.set([]);
  }

  /**
   * Datum (en bij `prefillWithTime` ook het uur) van `prefillDateUtc` in lokale tijd;
   * zonder prefill staan datum én tijd op nu (Steven 2026-10-24).
   */
  private applyPrefill(): void {
    const prefill = this.prefillDateUtc();
    const local = prefill ? moment.utc(prefill).local() : moment();
    this.dateValue.set(local.format('YYYY-MM-DD'));
    if (!prefill || this.prefillWithTime()) this.timeValue.set(local.format('HH:mm'));
    this.refreshDefaultName();
  }

  /**
   * I Tjing-smaak: een raadpleging heeft altijd een tijdstip. Zonder voorgevulde tijd
   * krijgt vandaag de huidige tijd en een andere dag 12:00; zonder datum vandaag.
   */
  private applyIChingPrefill(): void {
    this.applyPrefill();
    if (!this.dateValue()) this.dateValue.set(moment().format('YYYY-MM-DD'));
    if (this.timeValue()) return;
    const today = moment().format('YYYY-MM-DD');
    this.timeValue.set(this.dateValue() === today ? moment().format('HH:mm') : '12:00');
  }

  /** Alle standaardcharts; de eigen chart vooraan en voorgeselecteerd. */
  private async loadSourceCandidates(): Promise<void> {
    this.sourceLoading.set(true);
    try {
      const [charts, myChart] = await Promise.all([
        this.library.queryAllCharts(buildChartDataSourceQuery({
          page: 1,
          pageSize: 100,
          filterFields: [{ key: 'typeDescription', value: PlatformChartType.Standard }],
        })),
        this.groupService.getMyChart().catch(() => null),
      ]);
      if (!this.pickerMode()) return;
      const mine = myChart?.id ? myChart : null;
      const others = charts.filter(chart => chart.id && chart.id !== mine?.id);
      this.sourceCandidates.set(mine ? [mine, ...others] : others);
      if (mine?.id && this.sourceChartId() == null) {
        await this.onSourceChartChange(mine.id);
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.sourceLoading.set(false);
    }
  }

  /** Bronchart gekozen → document laden (natal-kern) en de default-naam laten volgen. */
  protected async onSourceChartChange(chartId: string | null): Promise<void> {
    this.sourceChartId.set(chartId);
    this.pickedSource.set(null);
    if (!chartId) return;
    const candidate = this.sourceCandidates().find(chart => chart.id === chartId);
    this.sourceLoading.set(true);
    try {
      const document = await this.library.getChartDocument(chartId);
      if (this.sourceChartId() !== chartId) return; // intussen een andere chart gekozen
      this.pickedSource.set({
        target: { chartId, chartName: candidate?.name ?? document.preset.name ?? '' },
        preset: document.preset,
      });
      this.refreshDefaultName();
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.sourceLoading.set(false);
    }
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chart.momentLinkDialog.title'),
      modeChart: translate('humanDesign.chart.momentLinkDialog.modeChart'),
      modeThought: translate('humanDesign.chart.momentLinkDialog.modeThought'),
      modeMood: translate('humanDesign.chart.momentLinkDialog.modeMood'),
      modeLifetime: translate('humanDesign.chart.momentLinkDialog.modeLifetime'),
      modeDetailed: translate('humanDesign.chart.momentLinkDialog.modeDetailed'),
      metaTitle: translate('humanDesign.chart.momentLinkDialog.metaTitle'),
      chartPlaceholder: translate('humanDesign.chart.momentLinkDialog.chartPlaceholder'),
      dateLabel: translate('humanDesign.chart.momentLinkDialog.dateLabel'),
      timeLabel: translate('humanDesign.chart.momentLinkDialog.timeLabel'),
      endDateLabel: translate('humanDesign.chart.momentLinkDialog.endDateLabel'),
      endTimeLabel: translate('humanDesign.chart.momentLinkDialog.endTimeLabel'),
      rangeHint: translate('humanDesign.chart.momentLinkDialog.rangeHint'),
      endBeforeStart: translate('humanDesign.chart.momentLinkDialog.endBeforeStart'),
      namePlaceholder: translate('humanDesign.chart.momentLinkDialog.namePlaceholder'),
      dayRangeHint: translate('humanDesign.chart.momentLinkDialog.dayRangeHint'),
      notePlaceholder: translate('humanDesign.chart.momentLinkDialog.notePlaceholder'),
      add: translate('humanDesign.chart.momentLinkDialog.add'),
      loading: translate('humanDesign.chartManager.detail.loading'),
      categoryLabel: translate('humanDesign.momentFields.categoryLabel'),
      colorLabel: translate('humanDesign.momentFields.colorLabel'),
      moodLabel: translate('humanDesign.momentFields.moodLabel'),
      vitalityLabel: translate('humanDesign.momentFields.vitalityLabel'),
      emotionsLabel: translate('humanDesign.momentFields.emotionsLabel'),
      iconLabel: translate('humanDesign.momentFields.iconLabel'),
      sourceChartLabel: translate('humanDesign.chart.momentLinkDialog.sourceChartLabel'),
      sourceChartPlaceholder: translate('humanDesign.chart.momentLinkDialog.sourceChartPlaceholder'),
      sourceChartHint: translate('humanDesign.chart.momentLinkDialog.sourceChartHint'),
      modeIChing: translate('humanDesign.chart.momentLinkDialog.modeIChing'),
      ichingTimeLabel: translate('humanDesign.chart.momentLinkDialog.ichingTimeLabel'),
      ichingHint: translate('humanDesign.chart.momentLinkDialog.ichingHint'),
      ichingStart: translate('humanDesign.chart.momentLinkDialog.ichingStart'),
      mindTitle: translate('humanDesign.chart.momentLinkDialog.mind.title'),
      mindIntro: translate('humanDesign.chart.momentLinkDialog.mind.intro'),
      mindTransitNeedsTime: translate('humanDesign.chart.momentLinkDialog.mind.transitNeedsTime'),
      mindTransitNone: translate('humanDesign.chart.momentLinkDialog.mind.transitNone'),
      mindChosen: (count: number) => this.transloco.translate<string>('humanDesign.chart.momentLinkDialog.mind.chosen', { count, max: HD_MIND_MAX_CONDITIONERS }, language),
      mindMaxReached: this.transloco.translate<string>('humanDesign.chart.momentLinkDialog.mind.maxReached', { max: HD_MIND_MAX_CONDITIONERS }, language),
      mindInsertText: translate('humanDesign.chart.momentLinkDialog.mind.insertText'),
      mindInsertTextTitle: translate('humanDesign.chart.momentLinkDialog.mind.insertTextTitle'),
      mindRefine: translate('humanDesign.chart.momentLinkDialog.mind.refine'),
      mindRefineHint: translate('humanDesign.chart.momentLinkDialog.mind.refineHint'),
      mindPersonLabel: translate('humanDesign.chart.momentLinkDialog.mind.personLabel'),
      mindCenterName: (center: HdBodygraphCenter) => translate(`humanDesign.properties.centerNames.${center}`),
      mindGate: (gate: number) => this.transloco.translate<string>('humanDesign.mindLog.gate', { gate }, language),
      mindPlanetName: (planetKey: string) => translate(`humanDesign.chart.birthWindow.planets.${planetKey}`),
      mindPersonPlaceholder: translate('humanDesign.chart.momentLinkDialog.mind.personPlaceholder'),
      mindPersonGatesLabel: translate('humanDesign.chart.momentLinkDialog.mind.personGatesLabel'),
      mindPersonNone: translate('humanDesign.chart.momentLinkDialog.mind.personNone'),
      mindCompletesChannel: translate('humanDesign.chart.momentLinkDialog.mind.completesChannel'),
      mindNotePlaceholder: translate('humanDesign.chart.momentLinkDialog.mind.notePlaceholder'),
    };
  });

  /** Placeholder van de notitie: de gedachte-aanzet in de smaak Gedachte. */
  protected readonly composerPlaceholder = computed(() => this.mode() === 'thought' ? this.text().mindNotePlaceholder : this.text().notePlaceholder);

  /** Alle typen kiesbaar (elke chart kan een moment zijn); alleen de chart zelf niet. */
  protected readonly chartOptions = computed(() => {
    const target = this.target();
    if (!target) return [];
    return this.candidates()
      .filter(chart => chart.id && chart.id !== target.chartId)
      .map(chart => ({ value: chart.id as string, label: chart.name ?? '' }));
  });

  /** De datum-smaak vergt de natal-kern uit het brondocument. */
  protected readonly dateModeAvailable = computed(() => this.effectiveSourcePreset() != null);

  /** Het geplande bereik zodra er een einddatum staat; null zolang de invoer geen range is. */
  private readonly plannedRange = computed(() => {
    const date = this.dateValue();
    const endDate = this.dateEndValue();
    if (!date || !endDate) return null;
    const start = moment(`${date}T${this.timeValue() || '00:00'}`, 'YYYY-MM-DDTHH:mm', true);
    const end = moment(`${endDate}T${this.timeEndValue() || '23:59'}`, 'YYYY-MM-DDTHH:mm', true);
    if (!start.isValid() || !end.isValid()) return null;
    return { start, end };
  });

  protected readonly rangeInvalid = computed(() => {
    const range = this.plannedRange();
    return range != null && !range.end.isAfter(range.start);
  });

  protected readonly sendDisabled = computed(() =>
    this.mode() === 'chart'
      ? !this.newChartId()
      : !this.dateModeAvailable() || !this.dateValue() || !this.nameValue().trim() || this.rangeInvalid());

  /** I Tjing-smaak: een chart (vast of gekozen) en een volledig tijdstip. */
  protected readonly ichingStartDisabled = computed(
    () => this.effectiveTarget() == null || !this.dateValue() || !this.timeValue(),
  );

  protected setMode(mode: HdMomentLinkMode): void {
    if (isHdMomentDateMode(mode) && !this.dateModeAvailable() && !this.pickerMode()) return;
    if (mode === 'iching' && !this.ichingAvailable()) return;
    if (mode === 'chart' && this.pickerMode()) return;
    this.mode.set(mode);
    this.error.set('');
    this.applyThoughtDefaults();
    if (mode === 'iching') this.applyIChingPrefill();
    else this.refreshDefaultName();
  }

  /** "Raadpleging starten": de host opent de I Tjing-momentdialoog voor deze chart op dit tijdstip. */
  protected startIChing(): void {
    const target = this.effectiveTarget();
    if (!target || this.ichingStartDisabled()) return;
    const local = moment(`${this.dateValue()}T${this.timeValue()}`, 'YYYY-MM-DDTHH:mm', true);
    if (!local.isValid()) return;
    this.close();
    this.ichingRequested.emit({ chartId: target.chartId, chartName: target.chartName, atUtc: local.toISOString() });
  }

  protected onDateChange(value: string): void {
    this.dateValue.set(value);
    this.refreshDefaultName();
  }

  protected onTimeChange(value: string): void {
    this.timeValue.set(value);
    this.refreshDefaultName();
  }

  protected onEndDateChange(value: string): void {
    this.dateEndValue.set(value);
    if (!value) this.timeEndValue.set('');
    this.refreshDefaultName();
  }

  protected onEndTimeChange(value: string): void {
    this.timeEndValue.set(value);
    this.refreshDefaultName();
  }

  protected onNameChange(value: string): void {
    this.nameValue.set(value);
    this.nameEdited = value.trim().length > 0;
  }

  /** Categorie-chip: nogmaals klikken wist de keuze (alles is optioneel). */
  protected toggleCategory(category: string): void {
    this.applyCategory(this.categoryValue() === category ? null : category);
  }

  /** Categorie (enkel in de smaak Gedetailleerd kiesbaar). */
  private applyCategory(category: string | null): void {
    this.categoryValue.set(category);
  }

  /** Transitchip aan/uit (tot het plafond); het centrum volgt de eerste keuze zolang de gebruiker er zelf geen koos. */
  protected toggleMindTransit(suggestion: HdMindGateSuggestion): void {
    const key = mindSuggestionKey(suggestion);
    const current = this.mindTransitKeys();
    if (current.includes(key)) {
      this.mindTransitKeys.set(current.filter(entry => entry !== key));
    } else {
      if (this.mindFull()) return;
      this.mindTransitKeys.set([...current, key]);
    }
    this.syncMindAutoCenter();
  }

  protected mindTransitSelected(suggestion: HdMindGateSuggestion): boolean {
    return this.mindTransitKeys().includes(mindSuggestionKey(suggestion));
  }

  /** Tooltip van een transitchip: kanaalafmaker, plafond bereikt, of het centrum. */
  protected mindSuggestionTitle(suggestion: HdMindGateSuggestion): string {
    const t = this.text();
    if (this.mindFull() && !this.mindTransitSelected(suggestion)) return t.mindMaxReached;
    const center = t.mindCenterName(suggestion.center);
    return suggestion.completesChannel ? `${center} · ${t.mindCompletesChannel}` : center;
  }

  /** Centrumkeuze onder Verfijnen: een eigen keuze overrulet het automatische centrum; leegmaken geeft het terug. */
  protected onMindCenterChange(center: HdBodygraphCenter | null): void {
    this.mindCenterManual = center != null;
    this.mindCenter.set(center);
    this.mindGate.set(null);
    if (!this.mindCenterManual) this.syncMindAutoCenter();
  }

  private syncMindAutoCenter(): void {
    if (this.mindCenterManual) return;
    const next = this.mindSelectedTransits()[0]?.center ?? null;
    if (next === this.mindCenter()) return;
    this.mindCenter.set(next);
    this.mindGate.set(null);
  }

  /** De gekozen conditioneerders als leesbare tekst: "Zon 64.3 (Hoofd), Ann (21, 40)". */
  protected buildMindInsertText(): string {
    const t = this.text();
    return this.buildMindConditioners()
      .map(conditioner => {
        const text = hdMindConditionerText(conditioner, planetKey => t.mindPlanetName(planetKey));
        if (conditioner.kind !== 'transit') return text;
        const center = this.mindSelectedTransits().find(s => s.planetKey === conditioner.planetKey && s.gate === conditioner.gate)?.center;
        return center ? `${text} (${t.mindCenterName(center)})` : text;
      })
      .join(', ');
  }

  /** "Zet in tekst": plakt de gekozen conditioneerders op de cursorpositie van de notitie (ronde 2). */
  protected insertMindText(): void {
    const text = this.buildMindInsertText();
    if (text) this.composer()?.insertText(text);
  }

  protected toggleMindPersonGate(gate: number): void {
    const current = this.mindPersonGates();
    this.mindPersonGates.set(current.includes(gate) ? current.filter(g => g !== gate) : [...current, gate].sort((a, b) => a - b));
  }

  /** Persoon gekozen → document laden (natal-activaties) voor de poortsuggesties. */
  protected async onMindPersonChange(chartId: string | null): Promise<void> {
    this.mindPersonChartId.set(chartId);
    this.mindPersonPreset.set(null);
    this.mindPersonGates.set([]);
    if (!chartId) return;
    const option = this.mindPersonOptions().find(candidate => candidate.value === chartId);
    this.mindPersonLoading.set(true);
    try {
      const document = await this.library.getChartDocument(chartId);
      if (this.mindPersonChartId() !== chartId) return;
      this.mindPersonPreset.set({ chartId, chartName: option?.label || document.preset.name || '', preset: document.preset });
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.mindPersonLoading.set(false);
    }
  }

  private async loadMindTransitActivations(natalPreset: HdBodygraphChartPreset, momentUtc: string): Promise<void> {
    const seq = ++this.mindTransitSeq;
    this.mindTransitLoading.set(true);
    try {
      const doc = await this.calculateMoment(natalPreset, momentUtc, this.nameValue().trim() || 'mind');
      if (seq !== this.mindTransitSeq) return;
      this.mindTransitActivations.set(filterHdJustNowPersonalityActivations(doc.planetaryActivations ?? []));
      // Eerder gekozen suggesties die op dit tijdstip niet meer bestaan, vervallen.
      const available = new Set(this.mindTransitSuggestions().map(mindSuggestionKey));
      const kept = this.mindTransitKeys().filter(key => available.has(key));
      if (kept.length !== this.mindTransitKeys().length) {
        this.mindTransitKeys.set(kept);
        this.syncMindAutoCenter();
      }
    } catch (e) {
      if (seq === this.mindTransitSeq) this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      if (seq === this.mindTransitSeq) this.mindTransitLoading.set(false);
    }
  }

  /** Transit van een tijdstip op de natal-kern van de bronchart (gedeeld door snapshot en mind-suggesties). */
  private calculateMoment(natalPreset: HdBodygraphChartPreset, momentUtc: string, name: string) {
    const core = extractCycleSourceCore(natalPreset.birthData);
    const birth = (natalPreset.birthData ?? {}) as {
      location?: { city?: string | null; country?: string | null } | null;
    };
    return this.library.calculateFromUtc({
      name,
      utc: momentUtc,
      timezone: core.timezone || 'UTC',
      location: {
        city: birth.location?.city ?? null,
        country: birth.location?.country ?? null,
        lat: core.latitude,
        lon: core.longitude,
      },
    });
  }

  /** De gekozen conditioneerders: transits in aantikvolgorde, dan de persoon (max. het plafond). */
  private buildMindConditioners(): HdMindConditioner[] {
    const conditioners: HdMindConditioner[] = this.mindSelectedTransits()
      .map(chosen => ({ kind: 'transit' as const, planetKey: chosen.planetKey, gate: chosen.gate, line: chosen.line }));
    const person = this.mindPersonPreset();
    if (person) conditioners.push({ kind: 'person', chartId: person.chartId, chartName: person.chartName, gates: this.mindPersonGates() });
    return conditioners.slice(0, HD_MIND_MAX_CONDITIONERS);
  }

  /** Het bewaarde mind-blok, of null wanneer het geen mind-notitie is of er niets gekozen werd. */
  private buildMindExtraJson(): string | null {
    if (!this.mindActive()) return null;
    const conditioners = this.buildMindConditioners();
    const center = this.mindCenter();
    const gate = this.mindGate();
    return joinHdMomentExtra({
      mind: {
        ...(center != null ? { center } : {}),
        ...(gate != null ? { gate } : {}),
        ...(conditioners.length > 0 ? { conditioners } : {}),
      },
    });
  }

  protected toggleVitality(vitality: number): void {
    this.vitalityValue.set(this.vitalityValue() === vitality ? null : vitality);
  }

  /** Emotie-chips: max 3; binnen een signpost-paar vervangt de tegenhanger (gedeelde helper). */
  protected toggleEmotion(key: string): void {
    this.emotionKeys.set(toggleHdMomentEmotion(this.emotionKeys(), key));
  }

  /** Chip uitgegrijsd op het maximum, behalve als hij een geselecteerde tegenhanger vervangt. */
  protected emotionDisabled(key: string): boolean {
    const current = this.emotionKeys();
    if (current.includes(key) || current.length < HD_MOMENT_MAX_EMOTIONS) return false;
    const counterpart = hdSignpostCounterpart(key);
    return counterpart == null || !current.includes(counterpart);
  }

  /** Default: "{chartnaam} {DD/MM/YY}" (+ " HH:mm" mét tijd; bij een range " {start} - {einde}"), in elke smaak. */
  private refreshDefaultName(): void {
    if (this.nameEdited) return;
    const target = this.effectiveTarget();
    const date = this.dateValue();
    if (!target || !date) {
      this.nameValue.set('');
      return;
    }
    const range = this.plannedRange();
    if (range != null) {
      const part = (value: moment.Moment, withTime: boolean) => value.format(withTime ? 'DD/MM/YY HH:mm' : 'DD/MM/YY');
      this.nameValue.set(
        `${target.chartName} ${part(range.start, !!this.timeValue())} - ${part(range.end, !!this.timeEndValue())}`,
      );
      return;
    }
    const time = this.timeValue();
    const local = moment(`${date}${time ? `T${time}` : ''}`, time ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD');
    if (!local.isValid()) return;
    // Altijd chartnaam + datum, ook bij een gedachte (Steven 2026-10-24).
    const prefix = target.chartName;
    this.nameValue.set(
      time
        ? buildMomentChartName(prefix, local.toISOString())
        : `${prefix} ${local.format('DD/MM/YY')}`,
    );
  }

  protected onOpenChange(open: boolean): void {
    if (!open) this.close();
  }

  private close(): void {
    this.target.set(null);
    this.openWithSourcePicker.set(false);
  }

  /** Composer-verzendknop = toevoegen; notitie en afbeeldingen zijn optioneel. */
  protected async onComposerSend(payload: SupportCommentPayload): Promise<void> {
    if (this.saving() || this.sendDisabled()) return;
    this.error.set('');
    this.saving.set(true);
    try {
      const note = payload.body.trim().slice(0, MOMENT_NOTE_MAX_LENGTH) || null;
      const attachments = payload.attachments.map(attachment => ({ ...attachment }));
      if (this.mode() === 'chart') {
        await this.linkExistingChart(note, attachments);
      } else {
        await this.createDateMoment(note, attachments);
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.saving.set(false);
    }
  }

  private async linkExistingChart(
    note: string | null,
    attachments: SupportCommentPayload['attachments'],
  ): Promise<void> {
    const target = this.target();
    const momentChartId = this.newChartId();
    if (!target || !momentChartId) return;
    // Richting: de gekozen chart ís het moment ("momentOf" → deze chart).
    await this.groupService.createRelation(momentChartId, target.chartId, 'momentOf', note, attachments);
    this.changed.emit(target.chartId);
    this.close();
  }

  /**
   * Moment op datum: mét tijdstip een berekende snapshot (personality van dat
   * moment via het bestaande compute-pad, zoals de cycles); zónder tijdstip een
   * dag-range-record. Bewust `createChart` + eigen relatie (niet de gedeelde
   * `createCalculatedChart`): de relatie draagt hier de notitie en bijlagen.
   */
  private async createDateMoment(
    note: string | null,
    attachments: SupportCommentPayload['attachments'],
  ): Promise<void> {
    const target = this.effectiveTarget();
    const natalPreset = this.effectiveSourcePreset();
    const date = this.dateValue();
    const name = this.nameValue().trim();
    if (!target || !natalPreset || !date || !name) return;

    const time = this.timeValue();
    const core = extractCycleSourceCore(natalPreset.birthData);
    let preset: HdBodygraphChartPreset;
    let tabTooltip: string;
    const range = this.plannedRange();

    if (range != null) {
      // Expliciete periode (stap 4): een range-record met lege activaties —
      // de tijdslider in de geopende chart berekent elke gekozen stand.
      const formatPart = (value: moment.Moment, withTime: boolean) =>
        new Intl.DateTimeFormat('nl-BE', withTime ? { dateStyle: 'medium', timeStyle: 'short' } : { dateStyle: 'medium' })
          .format(value.toDate());
      tabTooltip = `${formatPart(range.start, !!time)} tot ${formatPart(range.end, !!this.timeEndValue())}`;
      preset = buildTransitSnapshotPreset({
        natalPreset,
        natalChartId: target.chartId,
        momentUtc: range.start.toISOString(),
        momentEndUtc: range.end.toISOString(),
        personalityActivations: [],
        name,
        tabTooltip,
      });
    } else if (time) {
      const local = moment(`${date}T${time}`, 'YYYY-MM-DDTHH:mm', true);
      const momentUtc = local.toISOString();
      const doc = await this.calculateMoment(natalPreset, momentUtc, name);
      tabTooltip = new Intl.DateTimeFormat('nl-BE', { dateStyle: 'long', timeStyle: 'short' })
        .format(local.toDate());
      preset = buildTransitSnapshotPreset({
        natalPreset,
        natalChartId: target.chartId,
        momentUtc,
        personalityActivations: filterHdJustNowPersonalityActivations(doc.planetaryActivations ?? []),
        momentMetadata: doc.metadata ?? null,
        name,
        tabTooltip,
      });
    } else {
      // Dag zonder uur = range 00:00 t/m 23:59 lokale tijd (spec §4.6).
      const dayStart = moment(`${date}T00:00`, 'YYYY-MM-DDTHH:mm', true);
      const dayEnd = moment(`${date}T23:59`, 'YYYY-MM-DDTHH:mm', true);
      tabTooltip = new Intl.DateTimeFormat('nl-BE', { dateStyle: 'long' }).format(dayStart.toDate());
      preset = buildTransitSnapshotPreset({
        natalPreset,
        natalChartId: target.chartId,
        momentUtc: dayStart.toISOString(),
        momentEndUtc: dayEnd.toISOString(),
        personalityActivations: [],
        name,
        tabTooltip,
      });
    }

    const chart = await this.library.createChart({
      preset,
      name,
      type: PlatformChartType.Transit,
      visibility: PlatformChartVisibility.Private,
      icon: this.iconValue(),
      details: {
        color: this.colorValue(),
        category: this.effectiveCategory(),
        mood: this.moodValue(),
        vitality: this.vitalityValue(),
        emotions: joinHdMomentEmotions(this.emotionKeys()),
        momentExtraJson: this.buildMindExtraJson(),
      },
    });
    if (chart.id) {
      await this.groupService.createRelation(chart.id, target.chartId, 'momentOf', note, attachments);
    }
    this.changed.emit(target.chartId);
    this.close();
    // Nieuw aangemaakt moment standaard openen (wens Steven 2026-09-17).
    if (chart.id) {
      this.chartCreated.emit(chart.id);
    }
  }

  private async loadCandidates(chartId: string): Promise<void> {
    this.loading.set(true);
    try {
      const charts = await this.library.queryAllCharts(buildChartDataSourceQuery({ page: 1, pageSize: 100 }));
      if (this.target()?.chartId !== chartId) return;
      this.candidates.set(charts);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }
}

/** Stabiele sleutel van een suggestie (planeet, poort, lijn) voor de chip-selectie. */
function mindSuggestionKey(suggestion: HdMindGateSuggestion): string {
  return `${suggestion.planetKey}:${suggestion.gate}:${suggestion.line}`;
}
